import { useCallback, useMemo } from 'react';

import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';

import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useTierGroupProcessor } from 'pages/loyalty/loyalty-offers/tiered-offers/use-tier-group-processor';
import { mapOffersToTierGroup } from 'pages/loyalty/loyalty-offers/tiered-offers/utils';
import { selectors, useAppSelector } from 'state/global-state';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import {
  LoyaltyAppliedOffer,
  LoyaltyEngineId,
  LoyaltyOffer,
  SanityOfferListItemWithExtras,
} from 'state/loyalty/types';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';

const hasBlockingErrors = (errorCode: IncentiveEvaluationErrorCodes): boolean =>
  !FixableEvaluationErrors.has(errorCode);

const getOfferId = (
  offer: LoyaltyAppliedOffer | LoyaltyOffer | null | undefined
): string | undefined | null => {
  if (!offer) {
    return null;
  }
  if ('loyaltyEngineId' in offer) {
    return offer.loyaltyEngineId;
  } else if ('id' in offer) {
    return offer.id;
  }
  return null;
};

export function useLoyaltyOfferAvailabilityEvaluation() {
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const { processTierGroup } = useTierGroupProcessor();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);

  const cmsOffersMap = useMemo(() => getCmsOffersMapByCmsId(cmsOffers), [cmsOffers]);
  // console.debug('>>> cmsOffersMap', cmsOffersMap['a48ac94b-298b-49ae-a1fe-ef576b644712']);
  const offersToTierGroupMap = useMemo(
    () => mapOffersToTierGroup(cmsOffers as SanityOfferListItemWithExtras[]),
    [cmsOffers]
  );

  const findCmsOfferByCmsId = useCallback(
    (cmsId: string) => {
      return cmsOffersMap[cmsId] || null;
    },
    [cmsOffersMap]
  );

  const isOfferUnavailable = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer | null | undefined) => {
      // When the loyalty service is enabled, the backend owns availability and
      // omits unavailable offers from the response, so the front end never
      // re-derives availability (date/store, tier-group, non-fixable errors). A
      // returned offer is always available here; eviction instead keys on
      // absence from the settled batch response
      // (`useLoyaltyServiceAppliedOfferEviction`).
      if (loyaltyServiceOffersEnabled) {
        return false;
      }

      const offerId = getOfferId(offer);
      if (!offerId) {
        return true;
      }

      const tierGroup = offersToTierGroupMap[offerId as LoyaltyEngineId];
      if (tierGroup) {
        const { selectedOffer } = processTierGroup(tierGroup);
        if (!selectedOffer || (selectedOffer as any).loyaltyEngineId !== offerId) {
          // The offer belongs to a Tier Group but is not the selected offer or
          // there is no available tier for this store
          return true;
        }
      }

      const selectedCMSOffer =
        offer && 'cmsId' in offer && offer.cmsId ? findCmsOfferByCmsId(offer.cmsId) : null;

      const isSelectedLoyaltyOfferAvailable = selectedCMSOffer
        ? checkLoyaltyOfferAvailability(selectedCMSOffer)
        : false;

      const errorCodes = offersFeedbackMap[offerId];
      const someErrorIsNotFixable = errorCodes?.some(error =>
        hasBlockingErrors(error?.code as IncentiveEvaluationErrorCodes)
      );

      const isUnavailable = Boolean(
        (selectedCMSOffer && !isSelectedLoyaltyOfferAvailable) ||
          (errorCodes && someErrorIsNotFixable)
      );

      return isUnavailable;
    },
    [
      loyaltyServiceOffersEnabled,
      checkLoyaltyOfferAvailability,
      offersFeedbackMap,
      processTierGroup,
      findCmsOfferByCmsId,
      offersToTierGroupMap,
    ]
  );

  return { isOfferUnavailable, findCmsOfferByCmsId };
}
